import React, { useState, useEffect, useMemo } from 'react'
import AgentRksFilters from './AgentRksFilters'
import { Box, CircularProgress, Alert } from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { apiService } from '../../services/api'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, SubTabs, Icon, fmt,
  useDistinctFilters, buildOplFilterFields,
} from '../report-shell'

const statusColors = {
  VIP: { chip: 'success' },
  STANDARD: { chip: 'primary' },
  'ZERÓWKA': { chip: 'error' },
}

const OrangeVipStandardZero = ({
  title = 'Orange — Opaska SOS · VIP / Standard / Zero',
  subtitle = 'Klasyfikacja salonów po wolumenie sprzedaży: VIP (≥20 szt / 3 mies.), STANDARD, ZERÓWKA.',
  breadcrumb = ['KAM-SFA', 'Orange', 'Opaska SOS', 'VIP / Standard / Zero'],
  fetchData: fetchDataProp,
  fetchExport: fetchExportProp,
  exportFilenamePrefix = 'VIP_Standard_Zero_Orange',
  showPolkoweFilter = false,
} = {}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [activeKamTab, setActiveKamTab] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [polkoweOnly, setPolkoweOnly] = useState(false)
  const [filters, setFilters] = useState({})

  const fetchData = async (currentFilters = filters) => {
    try {
      setLoading(true)
      setError(null)
      const apiCall = fetchDataProp || apiService.getOrangeVipStandardZero
      const response = await apiCall(polkoweOnly, currentFilters)
      setData(response.data)
    } catch (err) {
      setError(`Błąd pobierania danych: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polkoweOnly])

  const onFiltersChange = (next) => {
    setFilters(next)
    fetchData(next)
  }

  const exportExcel = async () => {
    try {
      setExporting(true)
      const apiCall = fetchExportProp || apiService.getOrangeVipStandardZeroExport
      const response = await apiCall(polkoweOnly, filters)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${exportFilenamePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      let detail = err.message
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          const json = JSON.parse(text)
          detail = json.detail || text
        } catch (_) { /* ignore parse error */ }
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail
      }
      setError(`Błąd eksportu: ${detail}`)
    } finally {
      setExporting(false)
    }
  }

  // Build KAM tab list
  const kamTabs = useMemo(() => {
    if (!data) return ['Wszyscy']
    const kams = data.kam_summary.map(k => k.kam)
    return ['Wszyscy', ...kams]
  }, [data])

  // Filtered salons based on active KAM tab
  const filteredSalons = useMemo(() => {
    if (!data) return []
    if (activeKamTab === 0) return data.salons
    const selectedKam = kamTabs[activeKamTab]
    return data.salons.filter(s => (s.kam || '').toUpperCase().trim() === selectedKam)
  }, [data, activeKamTab, kamTabs])

  // ReportTable columns
  const columns = useMemo(() => {
    if (!data) return []

    const cols = [
      { field: 'kod_bscs', header: 'Kod BSCS', width: '1.4fr', minWidth: 180 },
      { field: 'region', header: 'Region', width: '140px' },
      { field: 'district', header: 'Dystrykt', width: '140px' },
    ]
    if (activeKamTab === 0) {
      cols.push({ field: 'kam', header: 'KAM', width: '110px' })
    }
    cols.push(
      { field: 'adres', header: 'Adres salonu', width: '160px' },
      { field: 'ch', header: 'CH', width: '140px' },
    )
    data.month_labels.forEach((label, i) => {
      cols.push({
        field: `__month_${i}`,
        header: label,
        width: '110px',
        sales: true,
      })
    })
    cols.push(
      { field: 'older', header: 'Starsze', width: '100px', numeric: true },
      { field: 'total', header: 'Total',   width: '100px', numeric: true },
      {
        field: 'status', header: 'Status', width: '130px',
        render: (v) => (
          <span style={{
            display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
            fontSize: 11, fontWeight: 600,
            background:
              v === 'VIP' ? '#dcfce7' : v === 'ZERÓWKA' ? '#fee2e2' : '#dbeafe',
            color:
              v === 'VIP' ? '#15803d' : v === 'ZERÓWKA' ? '#b91c1c' : '#1d4ed8',
            border: `1px solid ${
              v === 'VIP' ? '#86efac4d' : v === 'ZERÓWKA' ? '#fca5a54d' : '#93c5fd4d'}`,
          }}>{v}</span>
        ),
      },
    )
    return cols
  }, [data, activeKamTab])

  // ReportTable rows — flatten months[i] to top-level fields for sorting.
  const rows = useMemo(() => filteredSalons.map((s, idx) => {
    const flat = { id: s.kod_bscs || idx, ...s }
    if (data?.month_labels) {
      data.month_labels.forEach((_, i) => { flat[`__month_${i}`] = s.months?.[i] || 0 })
    }
    return flat
  }), [filteredSalons, data])

  // Chart data - sample every N days for performance
  const chartData = useMemo(() => {
    if (!data?.daily_cumulative?.length) return []
    const raw = data.daily_cumulative
    // Show every 7th day for readability, always include last point
    const step = Math.max(1, Math.floor(raw.length / 80))
    const sampled = []
    for (let i = 0; i < raw.length; i++) {
      if (i % step === 0 || i === raw.length - 1) {
        sampled.push({
          ...raw[i],
          label: raw[i].date.slice(5), // MM-DD
        })
      }
    }
    return sampled
  }, [data])

  const summary = data?.summary || {}
  const kam_summary = data?.kam_summary || []

  const distinct = useDistinctFilters('opl')
  const filterFields = [
    ...buildOplFilterFields(distinct, filters, onFiltersChange),
    ...(showPolkoweFilter ? [{
      type: 'toggle', key: 'polkoweOnly', label: 'Tylko salony półkowe',
      value: polkoweOnly, onChange: setPolkoweOnly,
    }] : []),
  ]

  const kpiTiles = data ? [
    { key: 'total',    label: 'Salony łącznie',         value: fmt.int(summary.total_salons || 0) },
    { key: 'vip',      label: 'VIP (≥20 szt / 3 mies.)', value: fmt.int(summary.vip_count || 0),      accent: '#16a34a' },
    { key: 'standard', label: 'STANDARD',                value: fmt.int(summary.standard_count || 0), accent: '#2563eb' },
    { key: 'zero',     label: 'ZERÓWKA',                 value: fmt.int(summary.zero_count || 0),     accent: '#dc2626' },
  ] : []

  return (
    <ReportShell
      title={title}
      subtitle={subtitle}
      breadcrumb={breadcrumb}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
          <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
        </HeaderButton>
      }
      filterBar={<FilterBar filters={filterFields} onReset={() => onFiltersChange({})} />}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
      {/* Cumulative Sales Chart */}
      {chartData.length > 0 && (
        <Section title="Sprzedaż dzień po dniu — narastająco" padded>
          <Box sx={{ height: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={10} interval="preserveStartEnd" />
                <YAxis />
                <RechartsTooltip labelFormatter={(_, payload) => {
                  if (!payload?.[0]?.payload?.date) return ''
                  const [y, m, d] = payload[0].payload.date.split('-')
                  return `${d}.${m}.${y}`
                }} />
                <Legend />
                <Area type="monotone" dataKey="z_usluga" name="Sprzedaż z usługą" stackId="1" stroke="#4caf50" fill="#a5d6a7" />
                <Area type="monotone" dataKey="bez_uslugi" name="Sprzedaż bez usługi" stackId="1" stroke="#ff9800" fill="#ffcc80" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      )}

      {(() => {
        const tabsItems = kamTabs.map((tab, i) => {
          if (i === 0) return { value: 0, label: `Wszyscy (${summary.total_salons})` }
          const ks = kam_summary.find(k => k.kam === tab)
          return { value: i, label: `${tab} (${ks?.total_salons || 0})` }
        })
        const ks = activeKamTab > 0 ? kam_summary.find(k => k.kam === kamTabs[activeKamTab]) : null
        const subtitle = ks
          ? `VIP: ${ks.vip_count} · STANDARD: ${ks.standard_count} · ZERÓWKA: ${ks.zero_count}`
          : null
        return (
          <Section
            title="Salony"
            subtitle={subtitle}
            right={<SubTabs tabs={tabsItems} value={activeKamTab} onChange={setActiveKamTab} />}
          >
            <ReportTable
              rows={rows}
              columns={columns}
              initialSort={{ field: 'total', dir: 'desc' }}
              pageSize={25}
              pageSizeOptions={[25, 50, 100]}
              density="compact"
            />
          </Section>
        )
      })()}
        </>
      )}
    </ReportShell>
  )
}

export default OrangeVipStandardZero
